import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TitresSecondaryService, TitresSecondaryDTO } from '../../../core/services/titres-secondary.service';
import { CustomAction, CustomTableHeader } from '../../../core/models/custom-table.model';
import { PageParams } from '../../../core/models/page.model';
import { DynamicTableComponent } from '../../../shared/components/dynamic-table/dynamic-table.component';
import { FileUploadModule } from 'primeng/fileupload';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { CardModule } from 'primeng/card';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { ToastService } from '../../../core/services/toast.service';
import { take } from 'rxjs';
import { SelectModule } from 'primeng/select';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DateRangeComponent } from '../../../shared/date-range/date-range.component';
import { UEMOA_COUNTRIES } from '../../../core/models/country.model';

@Component({
    selector: 'app-titres-secondary',
    imports: [
        CommonModule,
        DynamicTableComponent,
        FileUploadModule,
        ButtonModule,
        TooltipModule,
        CardModule,
        ConfirmDialogModule,
        SelectModule,
        FormsModule,
        ReactiveFormsModule,
        DateRangeComponent
    ],
    providers: [ConfirmationService],
    templateUrl: './titres-secondary.component.html',
    styleUrl: './titres-secondary.component.scss'
})
export class TitresSecondaryComponent implements OnInit, OnDestroy {
  private titresService = inject(TitresSecondaryService);
  private toastService = inject(ToastService);
  private confirmationService = inject(ConfirmationService);
  private fb = inject(FormBuilder);

  titres: TitresSecondaryDTO[] = [];
  columns: CustomTableHeader[] = [];
  pageable: PageParams = {
    page: 0,
    first: 0,
    rows: 10,
    totalRecords: 0,
    rowPerPageOptions: [10, 20, 50]
  };
  actions: CustomAction[] = [];
  countries = UEMOA_COUNTRIES;
  rangeDates: Date[] = [];
  minDate: Date | null = null;
  searchForm!: FormGroup;
  typeInstrumentOptions = [
    { label: 'OAT', value: 'OAT' },
    { label: 'BAT', value: 'BAT' }
  ];

  ngOnInit() {
    this.initColumns();
    this.initSearchForm();
    this.loadTitres();
    this.actions = this.getActions();
  }

  initSearchForm() {
    this.searchForm = this.fb.group({
      region: [''],
      typeInstrument: ['']
    });
  }

  ngOnDestroy() {
  }

  initColumns() {
    this.columns = [
      { column: 'Code ISIN', key: 'isin', type: 'text' },
      { column: 'Instrument', key: 'typeInstrument', type: 'text' },
      { column: 'Échéance', key: 'dueDate', type: 'date' },
      { column: 'Taux (%)', key: 'rate', type: 'text' },
      { column: 'Mode Amortissement', key: 'amortizationMode', type: 'text' },
      { column: 'Encours', key: 'outstandingAmount', type: 'number' },
    ];
  }

  loadTitres() {
    this.titresService.getAllTitres(this.pageable.page, this.pageable.rows)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          this.titres = response.content;
          this.pageable = {
            page: response.pageable.pageNumber,
            first: response.pageable.pageNumber * response.pageable.pageSize,
            rows: response.pageable.pageSize,
            totalRecords: response.totalElements,
            rowPerPageOptions: [10, 20, 50]
          };
        },
        error: (error) => {
          this.toastService.showToast('Erreur', 'Erreur lors du chargement des titres', 'error');
          console.error(error);
        }
      });
  }

  onPageChange(event: any) {
    this.pageable.page = event.page;
    this.pageable.rows = event.rows;
    this.pageable.first = event.first;
    this.loadTitres();
  }

  getActions(): CustomAction[] {
    return [
      {
        role: 'DELETE_TITRE_SECONDARY',
        icon: 'pi pi-trash',
        label: 'Supprimer',
        severity: 'danger',
        emit: (item) => this.deleteTitre(item),
        visible: true
      }
    ];
  }

  deleteTitre(titre: TitresSecondaryDTO) {
    this.confirmationService.confirm({
      message: `Êtes-vous sûr de vouloir supprimer ce titre (${titre.typeInstrument} - ${titre.isin}) ?`,
      header: 'Confirmation de suppression',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Oui',
      rejectLabel: 'Non',
      accept: () => {
        if (titre.id) {
          this.titresService.deleteTitre(titre.id)
            .pipe(take(1))
            .subscribe({
              next: () => {
                this.toastService.showToast('Succès', 'Titre supprimé avec succès', 'success');
                this.loadTitres();
              },
              error: (error) => {
                this.toastService.showToast('Erreur', 'Erreur lors de la suppression du titre', 'error');
                console.error(error);
              }
            });
        }
      }
    });
  }

  onUpload(event: any) {
    const file = event.files[0];
    if (file) {
      this.titresService.importFromExcel(file)
        .pipe(take(1))
        .subscribe({
          next: (response) => {
            this.toastService.showToast(
              'Succès',
              `${response.length} titre(s) importé(s) avec succès`,
              'success'
            );
            this.loadTitres();
            this.resetFileUpload();
          },
          error: (error) => {
            this.toastService.showToast('Erreur', 'Erreur lors de l\'import du fichier', 'error');
            console.error(error);
            this.resetFileUpload();
          }
        });
    }
  }

  resetFileUpload() {
    const fileUpload = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileUpload) {
      fileUpload.value = '';
    }
  }

  searchTitres() {
    // TODO: Implémenter la logique de recherche avec filtres
    this.loadTitres();
  }

  onDateRangeSelected(dates: Date[]) {
    this.rangeDates = dates;
  }

  onInvalidRange(errorMessage: string) {
    this.toastService.showToast('Erreur', errorMessage, 'error');
  }

  onReset() {
    this.searchForm.reset();
    this.rangeDates = [];
    this.loadTitres();
  }
}
